import * as React from 'react';
import classnames from 'classnames';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import SystemMessageWrapperContext from '../../wrapper/context/SystemMessageWrapperContext';
import SystemMessageNegotiationsReopenedDetailsOtherArrangement from './other-arrangement/SystemMessageNegotiationsReopenedDetailsOtherArrangement';

import { __t, applyTranslations } from 'utils/language';
import { addThousandsSeparator } from 'utils/helper/common';

import './system-message-negotiations-reopened-details.scss';

applyTranslations(require('./system-message-negotiations-reopened-details.i18n.json'));

import type { MessengerAdditionalCompensation } from 'types';
import type { SystemMessageNegotiationsReopenedDetailsType } from '../types';

type Props = {
  type: SystemMessageNegotiationsReopenedDetailsType,
  additionalCompensation: MessengerAdditionalCompensation
};

const MSG_KEY = 'system_message_negotiations_reopened_details';

const SystemMessageNegotiationsReopenedDetails: React.FC<Props> = ({
  type,
  additionalCompensation
}: Props) => {
  const context = React.useContext(SystemMessageWrapperContext);
  const { message } = context;
  const subcampaign = get(message, 'metadata.subcampaign', {});
  const { offer, updatedOffer } = subcampaign;
  const currentOffer = !isNil(updatedOffer) && type === 'new' ? updatedOffer : offer;

  const renderBudget = (): React.ReactNode => {
    const label = __t(`${MSG_KEY}.labels.${type}_budget_offer`);
    const text = `${label}: ${addThousandsSeparator(currentOffer?.budget || 0)}€`;

    return (
      <div
        className="system-message-negotiations-reopened-details__budget"
        title={text}
      >
        {text}
      </div>
    );
  };

  const renderVoucher = (): React.ReactNode => {
    if (isNil(additionalCompensation.voucherAmount)) {
      return null;
    }

    const amount = type === 'new'
      ? additionalCompensation.voucherAmount
      : additionalCompensation.voucherAmountOld;

    const text = `${__t(`${MSG_KEY}.labels.additional_voucher`)}: ${addThousandsSeparator(amount || 0)}€`;

    return (
      <div
        className="system-message-negotiations-reopened-details__voucher"
        title={text}
      >
        {text}
      </div>
    );
  };

  const renderOther = (): React.ReactNode => {
    if (isEmpty(additionalCompensation.otherDescription)) {
      return null;
    }

    const text = __t(`${MSG_KEY}.labels.other`);

    return (
      <div
        className="system-message-negotiations-reopened-details__other"
        title={`${text} ${__t(`${MSG_KEY}.labels.compensation`)}`}
      >
        {text}
      </div>
    );
  };

  const containerClasses = classnames('system-message-negotiations-reopened-details', {
    [`system-message-negotiations-reopened-details--${type}`]: true
  });

  if (currentOffer?.type === 'other') {
    return (
      <div className={containerClasses}>
        <SystemMessageNegotiationsReopenedDetailsOtherArrangement
          type={type}
        />
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      {renderBudget()}
      {renderVoucher()}
      {renderOther()}
    </div>
  );
};

export default SystemMessageNegotiationsReopenedDetails;
